'use client'

/**
 * Generador del certificado de diente caído del Ratón Pérez.
 *
 * La moneda se gasta y la carta se pierde; el diploma es lo que se pega en la
 * nevera y acaba en la caja de los recuerdos. El padre rellena el nombre (lo
 * demás es opcional) y se lo lleva terminado, en PNG o impreso.
 *
 * Privacidad: no hay ni una petición de red en todo el flujo. El nombre del
 * niño se queda en el dispositivo; el PNG se compone en un canvas local y la
 * analítica solo registra QUÉ campos se usaron, nunca su contenido.
 */

import { useEffect, useState } from 'react'
import { Stamp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  BABY_TEETH_TOTAL,
  certificateFilename,
  composeToothCertificate,
  type ToothCertificate,
} from '@/lib/guides/tools/tooth-certificate'
import type { ToothKind } from '@/lib/guides/tools/tooth-letter'
import { captureProductEvent, PRODUCT_EVENTS } from '@/lib/product-analytics'
import { CertificateSheet } from './certificate-sheet'
import { ToothPicker } from './tooth-letter-generator'
import { CallBridge } from './call-bridge'

const COPY = {
  heading: 'Haz el certificado de su diente',
  intro:
    'Rellena lo que sepas — solo el nombre es imprescindible — y te damos el diploma listo para imprimir o guardar. Nada de esto sale de tu móvil.',
  name: 'Nombre del niño o la niña',
  namePlaceholder: 'Lucía',
  tooth: '¿Qué diente es?',
  number: `¿Cuál de los ${BABY_TEETH_TOTAL}? (opcional)`,
  date: 'Fecha del diente (opcional)',
  howItFell: '¿Cómo se le cayó? (opcional)',
  howItFellPlaceholder: 'comiendo una manzana',
  generate: 'Crear el certificado',
  regenerate: 'Actualizar el certificado',
  nameRequired: 'Pon al menos el nombre y el ratón sella el resto.',
  ctaTitle: 'El certificado de {name} ya está firmado. ¿Se lo entrega en persona?',
  ctaBody:
    'El Ratón Pérez puede llamarle por videollamada: le felicita por su valentía, le llama por su nombre y le recuerda que se cepille.',
  ctaAction: 'Preparar la llamada del Ratón Pérez',
}

const ANALYTICS_BASE = {
  character_id: 'raton-perez',
  guide_slug: 'certificado-del-raton-perez',
  locale: 'es',
}

type FormState = {
  childName: string
  tooth: ToothKind
  toothNumber: string
  date: string
  howItFell: string
}

const EMPTY_FORM: FormState = {
  childName: '',
  tooth: 'first',
  toothNumber: '',
  date: '',
  howItFell: '',
}

/**
 * Hoy en ISO, en la zona del navegador.
 *
 * `toISOString()` daría el día en UTC, que de madrugada en América es el de
 * mañana — y este tráfico llega justo de noche. La fecha se compone a mano.
 */
function todayIso(): string {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

/** Fuera de la serie de veinte no es un número de pieza: se descarta. */
function parseOptionalToothNumber(raw: string): number | undefined {
  const parsed = Number.parseInt(raw, 10)
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= BABY_TEETH_TOTAL ? parsed : undefined
}

function CertificateForm({
  form,
  onChange,
  onSubmit,
  hasCertificate,
  showNameError,
}: {
  form: FormState
  onChange: (patch: Partial<FormState>) => void
  onSubmit: (event: React.FormEvent) => void
  hasCertificate: boolean
  showNameError: boolean
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label htmlFor="certificate-child-name">{COPY.name}</Label>
          <Input
            id="certificate-child-name"
            value={form.childName}
            onChange={(event) => onChange({ childName: event.target.value })}
            placeholder={COPY.namePlaceholder}
            autoComplete="off"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="certificate-date">{COPY.date}</Label>
          <Input
            id="certificate-date"
            type="date"
            value={form.date}
            onChange={(event) => onChange({ date: event.target.value })}
            className="mt-1"
          />
        </div>
      </div>

      <ToothPicker
        value={form.tooth}
        onChange={(tooth) => onChange({ tooth })}
        legend={COPY.tooth}
        name="certificate-tooth"
      />

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label htmlFor="certificate-tooth-number">{COPY.number}</Label>
          {/*
            Sin `min`/`max`: son restricciones nativas, y con ellas el navegador
            bloquea el envío del formulario entero por un campo OPCIONAL — te
            quedas sin certificado por un número mal escrito. El rango se aplica
            en `parseOptionalToothNumber`, que descarta lo imposible y sigue.
          */}
          <Input
            id="certificate-tooth-number"
            type="number"
            inputMode="numeric"
            value={form.toothNumber}
            onChange={(event) => onChange({ toothNumber: event.target.value })}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="certificate-how-it-fell">{COPY.howItFell}</Label>
          <Input
            id="certificate-how-it-fell"
            value={form.howItFell}
            onChange={(event) => onChange({ howItFell: event.target.value })}
            placeholder={COPY.howItFellPlaceholder}
            className="mt-1"
          />
        </div>
      </div>

      {showNameError && <p className="text-sm text-red-300">{COPY.nameRequired}</p>}

      <Button
        type="submit"
        className="rounded-full bg-white px-6 py-5 text-base font-semibold text-slate-900 hover:bg-white/90"
      >
        <Stamp className="size-4" aria-hidden />
        {hasCertificate ? COPY.regenerate : COPY.generate}
      </Button>
    </form>
  )
}

export function ToothCertificateGenerator() {
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [certificate, setCertificate] = useState<ToothCertificate | null>(null)
  const [childName, setChildName] = useState('')
  const [showNameError, setShowNameError] = useState(false)

  // La fecha se propone al montar, no en el estado inicial: la guía es estática
  // y el HTML se genera en el build, así que sembrarla ahí serviría la fecha del
  // despliegue a todo el mundo (y rompería la hidratación).
  useEffect(() => {
    setForm((current) => (current.date ? current : { ...current, date: todayIso() }))
  }, [])

  function handleChange(patch: Partial<FormState>) {
    setForm((current) => ({ ...current, ...patch }))
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    const name = form.childName.trim()
    if (!name) {
      setShowNameError(true)
      setCertificate(null)
      return
    }
    setShowNameError(false)
    const toothNumber = parseOptionalToothNumber(form.toothNumber)

    setCertificate(
      composeToothCertificate({
        childName: name,
        tooth: form.tooth,
        toothNumber,
        date: form.date,
        howItFell: form.howItFell,
      })
    )
    setChildName(name)
    captureProductEvent(PRODUCT_EVENTS.GUIDE_CERTIFICATE_CREATED, {
      ...ANALYTICS_BASE,
      tooth: form.tooth,
      // Qué campos opcionales usa la gente, sin mandar su contenido.
      has_tooth_number: toothNumber !== undefined,
      has_date: Boolean(form.date.trim()),
      has_how_it_fell: Boolean(form.howItFell.trim()),
    })
  }

  return (
    <div
      data-testid="tooth-certificate-generator"
      className="space-y-5 rounded-3xl border border-white/15 bg-white/5 p-6 md:p-8"
    >
      <div className="print:hidden">
        <h3 className="font-christmas text-xl font-bold text-white md:text-2xl">{COPY.heading}</h3>
        <p className="mt-2 text-base text-white/80">{COPY.intro}</p>
      </div>

      <div className="print:hidden">
        <CertificateForm
          form={form}
          onChange={handleChange}
          onSubmit={handleSubmit}
          hasCertificate={certificate !== null}
          showNameError={showNameError}
        />
      </div>

      {certificate && (
        <div className="space-y-4">
          <CertificateSheet
            certificate={certificate}
            filename={certificateFilename()}
            analyticsProperties={ANALYTICS_BASE}
          />
          <div className="print:hidden">
            <CallBridge
              childName={childName}
              character="raton-perez"
              locale="es"
              title={COPY.ctaTitle}
              body={COPY.ctaBody}
              action={COPY.ctaAction}
              placement="certificate_generator"
              analyticsProperties={ANALYTICS_BASE}
            />
          </div>
        </div>
      )}
    </div>
  )
}
